<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import axios from 'axios';

// State
const captions = ref([]);
const loading = ref(true);
const saving = ref(false);
const isEditing = ref(false);
const togglingId = ref(null);
const modalType = ref('error');
const errorModalContent = ref([]);
const originalData = ref(null);
let descEditor = null;

// Form state
const form = ref({
    id: null,
    title: '',
    subtitle: '',
    description: '',
    icon: ''
});

// Show modal helper
const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    try {
        const modalEl = document.getElementById('notificationModal');
        if (modalEl && typeof bootstrap !== 'undefined') {
            const modal = new bootstrap.Modal(modalEl);
            modal.show();
        }
    } catch (e) {
        alert(errorModalContent.value.join('\n'));
    }
};

// Normalize for comparison
const normalizeHtml = (html) => {
    if (!html) return '';
    return html.replace(/\s+/g, ' ').replace(/>\s+</g, '><').trim();
};

// Check if data changed
const isDataChanged = () => {
    if (!originalData.value) return true;
    
    // Get current description from CKEditor
    const currentDescription = descEditor ? descEditor.getData() : form.value.description;
    
    const currentTitle = (form.value.title || '').trim();
    const currentSubtitle = (form.value.subtitle || '').trim();
    const currentDesc = normalizeHtml(currentDescription);
    const currentIcon = (form.value.icon || 'fa fa-star').trim();
    
    const origTitle = (originalData.value.title || '').trim();
    const origSubtitle = (originalData.value.subtitle || '').trim();
    const origDesc = normalizeHtml(originalData.value.description || '');
    const origIcon = (originalData.value.icon || 'fa fa-star').trim();
    
    return currentTitle !== origTitle || 
           currentSubtitle !== origSubtitle || 
           currentDesc !== origDesc || 
           currentIcon !== origIcon;
};

// Methods
const loadCaptions = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/caption/get');
        if (response.data.status === 'success') {
            captions.value = response.data.data;
        }
    } catch (error) {
        console.error('Error loading captions:', error);
    } finally {
        loading.value = false;
    }
};

const editCaption = (caption) => {
    form.value = {
        id: caption.id,
        title: caption.title,
        subtitle: caption.subtitle,
        description: caption.description,
        icon: caption.icon || ''
    };
    originalData.value = { ...form.value };
    isEditing.value = true;
    
    nextTick(() => {
        initCKEditor();
        if (descEditor) {
            descEditor.setData(caption.description || '');
        }
    });
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const resetForm = () => {
    // Destroy CKEditor BEFORE hiding the form element
    if (descEditor) {
        try {
            descEditor.destroy();
        } catch (e) {
            console.warn('CKEditor destroy error:', e);
        }
        descEditor = null;
    }
    if (typeof CKEDITOR !== 'undefined' && CKEDITOR.instances['caption_description']) {
        try {
            CKEDITOR.instances['caption_description'].destroy();
        } catch (e) {
            console.warn('CKEditor instance destroy error:', e);
        }
    }
    
    isEditing.value = false;
    originalData.value = null;
    form.value = { id: null, title: '', subtitle: '', description: '', icon: '' };
};

const submitForm = async () => {
    saving.value = true;
    
    // Delay for spinner visibility
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Sync CKEditor data
    if (descEditor) {
        form.value.description = descEditor.getData();
    }
    
    // Validation
    const errors = [];
    if (!form.value.title?.trim()) errors.push('Title wajib diisi');
    if (!form.value.subtitle?.trim()) errors.push('Subtitle wajib diisi');
    if (!form.value.description?.trim()) errors.push('Description wajib diisi');
    
    if (errors.length > 0) {
        saving.value = false;
        showModal('error', errors);
        return;
    }
    
    // Check for changes
    if (isEditing.value && !isDataChanged()) {
        saving.value = false;
        showModal('warning', 'Tidak ada perubahan data yang disimpan.');
        return;
    }
    
    try {
        const response = await axios.put(`/admin/caption/${form.value.id}`, form.value);
        if (response.data.status === 'success') {
            resetForm();
            loadCaptions();
        } else if (response.data.status === 'warning') {
            showModal('warning', response.data.message);
        }
    } catch (error) {
        if (error.response?.data?.messages) {
            showModal('error', error.response.data.messages);
        } else {
            showModal('error', 'Terjadi kesalahan');
        }
    } finally {
        saving.value = false;
    }
};

const toggleStatus = async (caption) => {
    if (togglingId.value) return;
    togglingId.value = caption.id;
    try {
        const response = await axios.patch(`/admin/caption/${caption.id}/toggle-status`);
        if (response.data.status === 'success') {
            caption.active = response.data.data.active;
        }
    } catch (error) {
        showModal('error', 'Gagal mengubah status');
    } finally {
        togglingId.value = null;
    }
};

const initCKEditor = () => {
    if (typeof CKEDITOR === 'undefined') {
        console.error('CKEditor not loaded');
        return;
    }

    // Destroy existing instance
    if (CKEDITOR.instances['caption_description']) {
        CKEDITOR.instances['caption_description'].destroy();
    }

    // Initialize editor
    descEditor = CKEDITOR.replace('caption_description', {
        height: 150,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'links', items: ['Link', 'Unlink'] }
        ],
        removePlugins: 'elementspath',
        resize_enabled: false
    });
};

onMounted(() => {
    loadCaptions();
});

onBeforeUnmount(() => {
    if (descEditor) {
        descEditor.destroy();
        descEditor = null;
    }
    if (typeof CKEDITOR !== 'undefined' && CKEDITOR.instances['caption_description']) {
        CKEDITOR.instances['caption_description'].destroy();
    }
});
</script>

<template>
    <Head title="Kelola Caption" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola Caption Homepage</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item">
                                    <Link :href="route('dashboard.index')"><i class="mdi mdi-home-outline"></i></Link>
                                </li>
                                <li class="breadcrumb-item active" aria-current="page">Caption</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Form Panel -->
                <div class="col-xl-5 col-12">
                    <div class="card">
                        <form @submit.prevent="submitForm">
                            <div class="card-header">
                                <h4 class="card-title">
                                    <i class="ti-pencil-alt me-5"></i>{{ isEditing ? 'Edit' : 'Pilih' }} Caption
                                </h4>
                            </div>
                            <div class="card-body">
                                <div v-if="!isEditing" class="text-center text-muted p-5">
                                    <i class="fa fa-hand-pointer-o fa-3x mb-3"></i>
                                    <p>Klik tombol Edit pada tabel untuk mengedit caption</p>
                                </div>
                                <div v-else>
                                    <div class="form-group mb-3">
                                        <label for="caption_title" class="form-label">Title <span class="text-danger">*</span></label>
                                        <input id="caption_title" type="text" v-model="form.title" class="form-control">
                                    </div>
                                    
                                    <div class="form-group mb-3">
                                        <label for="caption_subtitle" class="form-label">Subtitle <span class="text-danger">*</span></label>
                                        <input id="caption_subtitle" type="text" v-model="form.subtitle" class="form-control">
                                    </div>
                                    
                                    <div class="form-group mb-3">
                                        <label for="caption_icon" class="form-label">Icon Class</label>
                                        <input id="caption_icon" type="text" v-model="form.icon" class="form-control" placeholder="fa fa-star">
                                        <small class="text-muted">Contoh: fa fa-car, fa fa-calendar, ti-star</small>
                                    </div>
                                    
                                    <div class="form-group mb-3">
                                        <label for="caption_description" class="form-label">Description <span class="text-danger">*</span></label>
                                        <textarea id="caption_description" v-model="form.description" class="form-control" rows="4"></textarea>
                                        <small class="text-muted">Deskripsi yang akan muncul di homepage</small>
                                    </div>
                                </div>
                            </div>
                            <div class="box-footer text-end" v-if="isEditing">
                                <button type="button" @click="resetForm" class="btn btn-warning me-2"><i class="ti-trash me-1"></i> Batal</button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                                    <i v-else class="ti-save-alt me-1"></i> Simpan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                
                <!-- Table Panel -->
                <div class="col-xl-7 col-12">
                    <div class="card">
                        <div class="card-header">
                            <h4 class="card-title"><i class="ti-view-list me-10"></i>Daftar Caption</h4>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center p-5">
                                <div class="spinner-border text-primary"></div>
                            </div>
                            <div v-else-if="captions.length === 0" class="text-center text-muted p-5">
                                Belum ada caption
                            </div>
                            <div v-else class="table-responsive">
                                <table class="table table-hover">
                                    <thead>
                                        <tr>
                                            <th width="50">No</th>
                                            <th>Title</th>
                                            <th>Subtitle</th>
                                            <th width="100">Status</th>
                                            <th width="100">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(caption, index) in captions" :key="caption.id" 
                                            :class="{ 'table-primary': isEditing && form.id === caption.id }">
                                            <td>{{ index + 1 }}</td>
                                            <td>
                                                <div class="d-flex align-items-center">
                                                    <i :class="[caption.icon || 'fa fa-star', 'text-primary me-2']"></i>
                                                    <strong>{{ caption.title }}</strong>
                                                </div>
                                            </td>
                                            <td>{{ caption.subtitle }}</td>
                                            <td>
                                                <button 
                                                    @click="toggleStatus(caption)" 
                                                    class="btn btn-sm rounded-1 px-3" 
                                                    :class="caption.active ? 'btn-success' : 'btn-danger'"
                                                    :disabled="togglingId === caption.id"
                                                    title="Klik untuk mengubah status">
                                                    <span v-if="togglingId === caption.id" class="spinner-border spinner-border-sm me-1"></span>
                                                    <i v-else :class="['fa me-1', caption.active ? 'fa-check-circle' : 'fa-times-circle']"></i>
                                                    {{ caption.active ? 'Aktif' : 'Nonaktif' }}
                                                </button>
                                            </td>
                                            <td>
                                                <button class="btn btn-outline-primary btn-sm rounded-1" @click="editCaption(caption)">
                                                    <i class="fa fa-edit me-1"></i> Edit
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Notification Modal -->
            <div class="modal fade" id="notificationModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header text-white" 
                            :class="{
                                'bg-danger': modalType === 'error',
                                'bg-warning text-dark': modalType === 'warning',
                                'bg-success': modalType === 'success',
                                'bg-info': modalType === 'info'
                            }">
                            <h5 class="modal-title">
                                <i class="me-2" :class="{
                                    'ti-alert': modalType === 'error',
                                    'fa fa-exclamation-triangle': modalType === 'warning',
                                    'fa fa-check-circle': modalType === 'success',
                                    'fa fa-info-circle': modalType === 'info'
                                }"></i>
                                {{ modalType === 'error' ? 'Peringatan Validasi' : modalType === 'warning' ? 'Peringatan' : modalType === 'success' ? 'Berhasil' : 'Informasi' }}
                            </h5>
                            <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut:</p>
                            <ul class="list-group list-group-flush">
                                <li class="list-group-item" 
                                    :class="{
                                        'list-group-item-danger': modalType === 'error',
                                        'list-group-item-warning': modalType === 'warning',
                                        'list-group-item-success': modalType === 'success',
                                        'list-group-item-info': modalType === 'info'
                                    }" 
                                    v-for="(msg, index) in errorModalContent" :key="index">
                                    <i class="fa me-2" :class="{
                                        'fa-exclamation-circle': modalType === 'error',
                                        'fa-exclamation-triangle': modalType === 'warning',
                                        'fa-check-circle': modalType === 'success',
                                        'fa-info-circle': modalType === 'info'
                                    }"></i>{{ msg }}
                                </li>
                            </ul>
                        </div>
                        <div class="modal-footer text-end">
                            <button type="button" class="btn" 
                                :class="{
                                    'btn-danger': modalType === 'error',
                                    'btn-warning': modalType === 'warning',
                                    'btn-success': modalType === 'success',
                                    'btn-info': modalType === 'info'
                                }" 
                                data-bs-dismiss="modal">
                                <i class="fa fa-check-square-o me-2"></i>Mengerti
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
